/**
 * Settings → Manage links — the `privacy` group of the manage-settings object,
 * as far as it concerns the short-link service (CC docket 0051, 2026-08-31).
 *
 * WHY A NEW TAB RATHER THAN AN EXISTING ONE. The docket asks for three fields "in
 * the privacy group of the Manage settings". That group has no UI in this app: the
 * manage work that landed here built the CUSTOMER-facing SPA (`src/manage/`), and
 * `getManageSettings` / `updateManageSettings` have had no consumer at all — grep
 * for either name before this file and the app returns nothing. So the fields
 * needed a home to be created, not found. This screen is deliberately the narrow
 * one: the three short-link settings, plus the booking-retention value they are
 * capped by, shown READ-ONLY because nothing in this app owns editing it yet.
 *
 * WHY THE INVALID RETENTION IS UNREACHABLE RATHER THAN REJECTED. `shortlink_
 * retention_days` may never exceed `privacy.retention_days` — a short link that
 * outlives the booking it points at turns a valid SMS into a dead end. The five
 * options are an enum on purpose (a free number field is a decision people skip),
 * and every option above the booking retention is DISABLED with the reason named,
 * so the 400 the endpoint would return is never the thing that teaches it.
 *
 * WHY `null` HOST IS NOT A RADIO OPTION. `shortlink_host` is nullable and the null
 * means "nobody has configured this", which is operationally different from "this
 * tenant deliberately stays on the shared domain". Collapsing them into one
 * "Platform" radio would make "who is still sending production traffic over the
 * shared fallback?" unanswerable. Unset renders as its own state above the choice.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Btn, Card, Icon, Input, Pill, RadioGroup, RadioGroupItem, SegmentedControl, T, Toggle, tx } from "@v0y4ge/jrni-ui";
import { PageHeader } from "../../shell/chrome";
import { can } from "../../shell/access";
import { toast } from "../../shell/toast";
import SettingsShell from "./SettingsShell";
import { manageSurface } from "@v0y4ge/api-client";
import { useT } from "../../lib/i18n";

/** The five values the platform accepts. Not a range, not a stepper. */
const RETENTION_DAYS = [30, 60, 90, 180, 365] as const;
type RetentionDays = (typeof RETENTION_DAYS)[number];
const DEFAULT_RETENTION: RetentionDays = 90;

type ShortlinkHost = null | { mode: "tenant" } | { mode: "custom"; domain: string };

/**
 * Read defensively rather than widening the contract. `ManageSettingsSchema` in
 * this kit's `api-contracts` predates the shortlink fields (grep: the composed kit
 * has no `shortlink_` anywhere in contracts or mocks), so the response object is
 * inspected as `unknown` instead. This reads itself out of existence the day the
 * regenerated contract lands. See the cc-outbox reply for the fixture gap.
 */
interface ShortlinkPrivacy {
  retention_days: number;
  shortlink_retention_days: RetentionDays;
  shortlink_host: ShortlinkHost;
  shortlink_include_scheme: boolean;
}

const asRetention = (v: unknown): RetentionDays =>
  (RETENTION_DAYS as readonly number[]).includes(Number(v)) ? (Number(v) as RetentionDays) : DEFAULT_RETENTION;

function readHost(v: unknown): ShortlinkHost {
  if (!v || typeof v !== "object") return null;
  const mode = (v as { mode?: unknown }).mode;
  if (mode === "tenant") return { mode: "tenant" };
  if (mode === "custom") return { mode: "custom", domain: String((v as { domain?: unknown }).domain ?? "") };
  return null;
}

function readPrivacy(settings: unknown): ShortlinkPrivacy {
  const p = (settings && typeof settings === "object" ? (settings as { privacy?: unknown }).privacy : null) as Record<string, unknown> | null;
  const booking = Number(p?.retention_days);
  return {
    retention_days: Number.isFinite(booking) && booking > 0 ? booking : 3650,
    shortlink_retention_days: asRetention(p?.shortlink_retention_days),
    shortlink_host: readHost(p?.shortlink_host),
    shortlink_include_scheme: p?.shortlink_include_scheme === true,
  };
}

export default function SettingsManageLinks() {
  const t = useT();
  const canWrite = can("settings.write");
  /** The whole settings object, so a POST carries back everything it arrived with. */
  const [raw, setRaw] = useState<Record<string, unknown> | null>(null);
  const [form, setForm] = useState<ShortlinkPrivacy | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await manageSurface.getManageSettings("appointment");
        const settings = (res as unknown as { data?: { settings?: unknown } }).data?.settings ?? {};
        if (!live) return;
        setRaw(settings as Record<string, unknown>);
        setForm(readPrivacy(settings));
      } catch (e) {
        console.warn("getManageSettings failed:", e);
        if (live) setError(t("settings.manageLinks.loadFailed", "Couldn't load manage settings. Retry in a moment."));
      }
    })();
    return () => { live = false; };
  }, [t]);

  const stored = useMemo(() => (raw ? readPrivacy(raw) : null), [raw]);
  const dirty = !!form && !!stored && (
    form.shortlink_retention_days !== stored.shortlink_retention_days
    || form.shortlink_include_scheme !== stored.shortlink_include_scheme
    || JSON.stringify(form.shortlink_host) !== JSON.stringify(stored.shortlink_host)
  );

  const set = (patch: Partial<ShortlinkPrivacy>) => setForm((f) => (f ? { ...f, ...patch } : f));

  const onSave = useCallback(async () => {
    if (!form || !raw) return;
    setSaving(true);
    try {
      const privacy = { ...(raw.privacy as Record<string, unknown> | undefined), 
        shortlink_retention_days: form.shortlink_retention_days,
        shortlink_host: form.shortlink_host,
        shortlink_include_scheme: form.shortlink_include_scheme };
      const next = { ...raw, privacy };
      const res = await manageSurface.updateManageSettings(
        "appointment",
        { settings: next } as unknown as Parameters<typeof manageSurface.updateManageSettings>[1],
      );
      const settings = (res as unknown as { data?: { settings?: unknown } }).data?.settings ?? next;
      setRaw(settings as Record<string, unknown>);
      setForm(readPrivacy(settings));
      toast({ tone: "success", title: t("settings.manageLinks.saved", "Short-link settings saved") });
    } catch (e) {
      console.warn("updateManageSettings failed:", e);
      toast({
        tone: "danger",
        title: t("settings.manageLinks.saveFailed", "Couldn't save short-link settings"),
        subtitle: t("settings.manageLinks.saveFailedSub", "Nothing changed — your edits are still here. Retry in a moment."),
      });
    } finally {
      setSaving(false);
    }
  }, [form, raw, t]);

  const host = form?.shortlink_host ?? null;
  const hostValue = host === null ? null : host.mode;

  return (
    <SettingsShell activeTab="manageLinks">
      <PageHeader
        title={t("settings.manageLinks.title", "Manage links")}
        subtitle={t("settings.manageLinks.subtitle", "The short links that stand in for the manage URL in SMS — where they live, and how long they outlive the appointment.")}
        actions={canWrite ? (
          <Btn onClick={onSave} disabled={!dirty || saving}>
            {dirty ? t("account.details.saveChanges", "Save changes") : t("workforce.coaching.saved", "Saved")}
          </Btn>
        ) : null}
      />
      <div style={{ flex: 1, overflow: "auto", padding: `${T.space[5]} ${T.space[6]} ${T.space[10]}`, display: "flex", flexDirection: "column", gap: T.space[4], maxWidth: 760 }}>
        {error ? (
          <Card style={{ padding: T.space[4], display: "flex", alignItems: "center", gap: T.space[3], color: T.red1 }}>
            <Icon.warn size={14} /> <span style={{ fontSize: T.text.bodySm.fontSize }}>{error}</span>
          </Card>
        ) : null}

        {!form ? (
          <Card style={{ padding: T.space[4], fontSize: T.text.bodySm.fontSize, color: T.soft }}>
            {t("common.status.loading", "Loading…")}
          </Card>
        ) : (
          <>
            {/* ── Retention ─────────────────────────────────────────────── */}
            <Card style={{ padding: T.space[4], display: "flex", flexDirection: "column", gap: T.space[3] }}>
              <div>
                <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>
                  {t("settings.manageLinks.retentionLabel", "Delete short links after the appointment")}
                </h3>
                <p style={{ ...tx.help, margin: `${T.space[1]} 0 0`, fontSize: T.text.label.fontSize }}>
                  {t("settings.manageLinks.retentionHelp", "Counted from the END OF THE APPOINTMENT, not from the day the booking was made or the link created. After that the link stops resolving and the row is deleted.")}
                </p>
              </div>
              <SegmentedControl<string>
                ariaLabel={t("settings.manageLinks.retentionLabel", "Delete short links after the appointment")}
                value={String(form.shortlink_retention_days)}
                onChange={(v) => set({ shortlink_retention_days: asRetention(v) })}
                options={RETENTION_DAYS.map((d) => ({
                  value: String(d),
                  label: t("settings.manageLinks.nDays", "{n} days").replace("{n}", String(d)),
                  disabled: !canWrite || d > form.retention_days,
                }))}
              />
              <div style={{ display: "flex", alignItems: "baseline", gap: T.space[2], fontSize: T.text.label.fontSize, color: T.soft, flexWrap: "wrap" }}>
                <Pill tone="neutral">{t("settings.manageLinks.bookingRetention", "Booking retention: {n} days").replace("{n}", String(form.retention_days))}</Pill>
                <span>
                  {t("settings.manageLinks.capHelp", "A short link can't outlive the booking it points at, so anything longer than the booking retention isn't selectable. Booking retention is set by your data-retention policy, not here.")}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: T.text.bodySm.fontSize, color: T.n2, lineHeight: 1.5 }}>
                {t("settings.manageLinks.retentionSentence", "A link for an appointment ending 14 October stops working on {date} — {n} days after the appointment.")
                  .replace("{n}", String(form.shortlink_retention_days))
                  .replace("{date}", new Date(Date.UTC(2026, 9, 14) + form.shortlink_retention_days * 86400000)
                    .toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }))}
              </p>
            </Card>

            {/* ── Host ──────────────────────────────────────────────────── */}
            <Card style={{ padding: T.space[4], display: "flex", flexDirection: "column", gap: T.space[3] }}>
              <div>
                <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("settings.manageLinks.hostLabel", "Short-link domain")}</h3>
                <p style={{ ...tx.help, margin: `${T.space[1]} 0 0`, fontSize: T.text.label.fontSize }}>
                  {t("settings.manageLinks.hostHelp", "Carriers filter shared shortener domains — one noisy sender on the shared domain can take every other tenant on it down with them. A configured domain is the intended production state.")}
                </p>
              </div>

              {host === null ? (
                <div style={{ display: "flex", alignItems: "flex-start", gap: T.space[3], padding: `${T.space[3]} ${T.space[3]}`, borderRadius: T.radius.md, background: T.n6, border: `1px dashed ${T.border}` }}>
                  <Icon.warn size={14} style={{ color: T.orange1, flexShrink: 0, marginTop: 2 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: T.text.bodySm.fontSize, fontWeight: 700, color: T.n1 }}>
                      {t("settings.manageLinks.hostUnset", "Not configured — using the shared jrni.ai domain")}
                    </div>
                    <div style={{ fontSize: T.text.label.fontSize, color: T.soft, marginTop: T.space[1], lineHeight: 1.5 }}>
                      {t("settings.manageLinks.hostUnsetHelp", "Links currently send as l.jrni.ai/… — the fallback a demo or a freshly provisioned tenant gets. Pick a domain below to leave it.")}
                    </div>
                  </div>
                </div>
              ) : null}

              <RadioGroup
                name="shortlink-host"
                value={hostValue}
                onChange={(v) => { if (v === "tenant") set({ shortlink_host: { mode: "tenant" } }); }}
              >
                <RadioGroupItem
                  value="tenant"
                  label={t("settings.manageLinks.hostTenant", "Your tenant subdomain")}
                  supportingText={t("settings.manageLinks.hostTenantHelp", "l.<tenant>.jrni.ai — isolated from other tenants' sending reputation. No verification needed.")}
                  state={canWrite ? "default" : "disabled"}
                />
                <RadioGroupItem
                  value="custom"
                  label={t("settings.manageLinks.hostCustom", "Your own domain")}
                  supportingText={t("settings.manageLinks.hostCustomHelp", "e.g. l.bank.example — needs domain verification, which isn't available yet. We'll open this when it lands.")}
                  state="disabled"
                />
              </RadioGroup>

              {host !== null && host.mode === "custom" ? (
                <Input
                  label={t("settings.manageLinks.hostCustomDomain", "Custom domain")}
                  value={host.domain}
                  readOnly
                  onChange={() => undefined}
                />
              ) : null}

              {host !== null ? (
                <div>
                  <Btn variant="secondary" size="sm" disabled={!canWrite} onClick={() => set({ shortlink_host: null })}>
                    {t("settings.manageLinks.hostClear", "Clear — back to not configured")}
                  </Btn>
                </div>
              ) : null}
            </Card>

            {/* ── Scheme ────────────────────────────────────────────────── */}
            <Card style={{ padding: T.space[4], display: "flex", flexDirection: "column", gap: T.space[3] }}>
              <label style={{ display: "flex", alignItems: "center", gap: T.space[3], cursor: canWrite ? "pointer" : "default" }}
                onClick={() => canWrite && set({ shortlink_include_scheme: !form.shortlink_include_scheme })}>
                <Toggle on={form.shortlink_include_scheme} />
                <span style={{ fontSize: T.text.body.fontSize, color: T.n1, fontWeight: 600 }}>
                  {t("settings.manageLinks.schemeLabel", "Include https:// in the message")}
                </span>
                <span style={{ marginLeft: "auto", fontFamily: T.fontMono, fontSize: T.text.label.fontSize, color: T.soft }}>
                  {form.shortlink_include_scheme ? "https://l.jrni.ai/aK7qP3zM9x" : t("settings.manageLinks.text", "l.jrni.ai/aK7qP3zM9x")}
                </span>
              </label>
              <p style={{ margin: 0, fontSize: T.text.label.fontSize, color: T.soft, lineHeight: 1.55 }}>
                {t("settings.manageLinks.schemeHelp", "Off saves eight characters — more than any shortening of the code itself buys — and iOS, Android and WhatsApp all turn a bare domain with a known TLD into a link anyway. Turn it on if your recipients use a client that doesn't, and would otherwise be left typing the address by hand.")}
              </p>
            </Card>

            <p style={{ margin: 0, fontSize: T.text.label.fontSize, color: T.soft, lineHeight: 1.55 }}>
              {t("settings.manageLinks.mintNote", "A short link is only created when a template actually uses the Manage link (short) token. A template that doesn't reference it mints nothing — no row, no retention question.")}
            </p>
          </>
        )}
      </div>
    </SettingsShell>
  );
}
